import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { KeyRound, Loader2, CheckCircle2, XCircle, Save, Eye, EyeOff, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODELS = ['gpt-4o', 'gpt-4o-mini', 'gpt-4.1', 'gpt-4-turbo'];

const SuperAdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [maskedKey, setMaskedKey] = useState('');
  const [model, setModel] = useState('gpt-4o');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  // Email (Resend) settings
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [emailMasked, setEmailMasked] = useState('');
  const [defaultFrom, setDefaultFrom] = useState('onboarding@resend.dev');
  const [emailKey, setEmailKey] = useState('');
  const [showEmailKey, setShowEmailKey] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchEmailSettings();
  }, []);

  const fetchEmailSettings = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/settings/email`);
      setEmailConfigured(res.data.configured);
      setEmailMasked(res.data.maskedKey || '');
      setDefaultFrom(res.data.defaultFrom || 'onboarding@resend.dev');
    } catch (e) { /* silent */ }
  };

  const saveEmail = async () => {
    if (!emailKey.trim()) {
      toast.error('Enter a Resend API key to save');
      return;
    }
    setEmailSaving(true);
    try {
      const res = await axios.put(`${API}/superadmin/settings/email`, { apiKey: emailKey.trim(), defaultFrom });
      setEmailConfigured(res.data.configured);
      setEmailMasked(res.data.maskedKey || '');
      setDefaultFrom(res.data.defaultFrom || defaultFrom);
      setEmailKey('');
      toast.success('Email provider saved — assignment & comment emails are now active');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save email settings');
    } finally {
      setEmailSaving(false);
    }
  };

  const clearEmail = async () => {
    if (!window.confirm('Remove the email provider key? Outbound emails will stop (in-app notifications continue).')) return;
    setEmailSaving(true);
    try {
      const res = await axios.put(`${API}/superadmin/settings/email`, { apiKey: '', defaultFrom });
      setEmailConfigured(res.data.configured);
      setEmailMasked('');
      toast.success('Email provider removed');
    } catch (e) {
      toast.error('Failed to remove email key');
    } finally {
      setEmailSaving(false);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/settings/llm`);
      setConfigured(res.data.configured);
      setMaskedKey(res.data.maskedKey || '');
      setModel(res.data.model || 'gpt-4o');
    } catch (e) {
      toast.error('Failed to load LLM settings');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!apiKey.trim()) {
      toast.error('Enter an API key to save');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/superadmin/settings/llm`, { apiKey: apiKey.trim(), model });
      setConfigured(res.data.configured);
      setMaskedKey(res.data.maskedKey || '');
      setModel(res.data.model || model);
      setApiKey('');
      toast.success('LLM key saved — vision pipeline is now active');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save LLM settings');
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async () => {
    if (!window.confirm('Remove the platform LLM key? Vision analysis will stop working until a new key is set.')) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/superadmin/settings/llm`, { apiKey: '', model });
      setConfigured(res.data.configured);
      setMaskedKey('');
      toast.success('LLM key removed');
    } catch (e) {
      toast.error('Failed to remove key');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Platform Settings</h1>
          <p className="text-slate-600 mt-1">Configure the AI vision provider used across all tenants</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="llm-settings-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">OpenAI Vision Key</h2>
              <p className="text-sm text-slate-500">Powers Object Intelligence, batch, 360° and 3D surface analysis</p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 py-6"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
          ) : (
            <>
              <div className={`flex items-center gap-2 mb-5 px-3 py-2 rounded-lg text-sm font-medium ${configured ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`} data-testid="llm-status">
                {configured ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {configured ? `Configured (${maskedKey})` : 'Not configured — vision analysis is disabled'}
              </div>

              <label className="block text-sm font-medium text-slate-700 mb-1">API Key</label>
              <div className="relative mb-4">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="sk-..."
                  className="w-full px-4 py-2 pr-10 bg-slate-50 border border-slate-300 rounded-lg font-mono text-sm"
                  data-testid="llm-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowKey((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                  data-testid="llm-key-toggle"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <label className="block text-sm font-medium text-slate-700 mb-1">Model</label>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg mb-5"
                data-testid="llm-model-select"
              >
                {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>

              <div className="flex gap-3">
                <button
                  onClick={save}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60"
                  data-testid="llm-save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Key
                </button>
                {configured && (
                  <button
                    onClick={clearKey}
                    disabled={saving}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-60"
                    data-testid="llm-clear-btn"
                  >
                    Remove Key
                  </button>
                )}
              </div>

              <p className="text-xs text-slate-400 mt-4">
                The key is stored securely on the server and never shown in full again. Provide your own OpenAI key here
                (or during deployment) to activate all AI vision features.
              </p>
            </>
          )}
        </div>

        {/* Email provider (Resend) */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="email-settings-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Mail className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Email Notifications (Resend)</h2>
              <p className="text-sm text-slate-500">Sends emails when a punch-list issue is assigned or commented</p>
            </div>
          </div>

          <div className={`flex items-center gap-2 mb-5 px-3 py-2 rounded-lg text-sm font-medium ${emailConfigured ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`} data-testid="email-status">
            {emailConfigured ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {emailConfigured ? `Configured (${emailMasked})` : 'Not configured — only in-app notifications are sent'}
          </div>

          <label className="block text-sm font-medium text-slate-700 mb-1">Resend API Key</label>
          <div className="relative mb-4">
            <input
              type={showEmailKey ? 'text' : 'password'}
              value={emailKey}
              onChange={(e) => setEmailKey(e.target.value)}
              placeholder="re_..."
              className="w-full px-4 py-2 pr-10 bg-slate-50 border border-slate-300 rounded-lg font-mono text-sm"
              data-testid="email-key-input"
            />
            <button type="button" onClick={() => setShowEmailKey((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600" data-testid="email-key-toggle">
              {showEmailKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <label className="block text-sm font-medium text-slate-700 mb-1">Default Sender</label>
          <input
            value={defaultFrom}
            onChange={(e) => setDefaultFrom(e.target.value)}
            placeholder="onboarding@resend.dev"
            className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg mb-5 text-sm"
            data-testid="email-from-input"
          />

          <div className="flex gap-3">
            <button onClick={saveEmail} disabled={emailSaving} className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-60" data-testid="email-save-btn">
              {emailSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Provider
            </button>
            {emailConfigured && (
              <button onClick={clearEmail} disabled={emailSaving} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-60" data-testid="email-clear-btn">
                Remove Key
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-4">
            Each tenant sets its own verified "from" address under Company → Profile. The default sender is used until they do.
            Provide the Resend key at deployment to enable outbound email.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default SuperAdminSettings;
